import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  TextField as MuiTextField,
  Slider,
  Switch,
  Tooltip,
} from '@mui/material'

import { ChevronDownIcon } from '~assets/icons'
import { Icon } from '~components/icons'
import { Dropdown, InputLabelStyled, Typography } from '~components/internal'
import {
  type PhonicBackgroundNoise,
  type PhonicPronunciationDictionaryEntry,
  type PhonicVoice,
} from '~types'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface PhonicVoiceConfigProps {
  // Voice
  voiceId: string | null
  onVoiceChange: (voiceId: string | null) => void
  voices: PhonicVoice[]
  audioSpeed: number | null
  onAudioSpeedChange: (v: number) => void

  // Conversation behavior
  isWelcomeMessageInterruptible: boolean
  onIsWelcomeMessageInterruptibleChange: (v: boolean) => void
  minWordsToInterrupt: number
  onMinWordsToInterruptChange: (v: number) => void
  enableAssistantBackchannel: boolean
  onEnableAssistantBackchannelChange: (v: boolean) => void
  assistantBackchannelAggressiveness: number
  onAssistantBackchannelAggressivenessChange: (v: number) => void

  // Ambience
  backgroundNoise: PhonicBackgroundNoise | null
  onBackgroundNoiseChange: (v: PhonicBackgroundNoise | null) => void
  backgroundNoiseLevel: number
  onBackgroundNoiseLevelChange: (v: number) => void

  // Speech recognition
  boostedKeywords: string[]
  onBoostedKeywordsChange: (v: string[]) => void
  pronunciationDictionary: PhonicPronunciationDictionaryEntry[]
  onPronunciationDictionaryChange: (v: PhonicPronunciationDictionaryEntry[]) => void
  onRegisterPronunciationFlush?: (flush: () => void) => void
}

// ─── Tooltip copy ────────────────────────────────────────────────────────────

const TOOLTIPS = {
  voice:
    'The voice callers will hear. Use the play button next to each voice to preview it before choosing.',
  speed:
    'How fast the assistant speaks. 1.0 is a natural pace; higher sounds quicker, lower sounds slower.',
  interruptibleGreeting:
    'When on, callers can talk over the opening greeting. Turn off to make sure the full greeting plays before the assistant starts listening.',
  minWordsToInterrupt:
    'How many words a caller must say before the assistant stops talking. Raise this so coughs or a quick "uh-huh" don\'t cut the assistant off.',
  backchannel:
    'Lets the assistant make short listening sounds like "mm-hmm" while the caller is speaking, so the conversation feels more natural.',
  backchannelAggressiveness:
    'How often the assistant uses those listening sounds. Lower is subtle, higher is frequent. Only applies when Backchannel is on.',
  backgroundNoise:
    "Adds subtle ambient sound (office, call center, coffee shop) behind the assistant's voice so calls feel less sterile. Choose None for clean audio.",
  noiseLevel:
    "How loud the ambient noise is. Keep it low so it never competes with the assistant's voice. Only applies when a background noise is selected.",
  boostedKeywords:
    'Words or short phrases the assistant should recognize more accurately, such as street names, department names, or local terms.',
  pronunciation:
    'Teach the assistant how to say words it may get wrong, such as place names. Enter the word, then spell out how it should sound (e.g. Phuket → Poo-ket).',
}

const ROW_SX = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 } as const

// ─── Subcomponents ───────────────────────────────────────────────────────────

const InfoTooltip: React.FC<{ title: string }> = ({ title }) => (
  <Tooltip title={title} arrow placement='top' enterTouchDelay={0}>
    <InfoOutlinedIcon
      tabIndex={0}
      // stops the icon from toggling a parent <label> (e.g. FormControlLabel)
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
      sx={{
        fontSize: 14,
        color: 'text.disabled',
        cursor: 'help',
        '&:hover, &:focus-visible': { color: 'text.secondary' },
      }}
    />
  </Tooltip>
)

const SettingField: React.FC<{
  label: string
  tooltip: string
  children: React.ReactNode
}> = ({ label, tooltip, children }) => (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <InputLabelStyled shrink>{label}</InputLabelStyled>
      <InfoTooltip title={tooltip} />
    </Box>
    {children}
  </Box>
)

const InlineSlider: React.FC<{
  label: string
  tooltip: string
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  minLabel: string
  maxLabel: string
  formatValue: (v: number) => string
  step?: number
  disabled?: boolean
}> = ({
  label,
  tooltip,
  value,
  onChange,
  min,
  max,
  minLabel,
  maxLabel,
  formatValue,
  step = 0.01,
  disabled = false,
}) => (
  <Box sx={{ opacity: disabled ? 0.38 : 1, transition: 'opacity 0.2s' }}>
    <Box
      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.25 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant='caption' sx={{ fontWeight: 500, color: 'text.primary' }}>
          {label}
        </Typography>
        <InfoTooltip title={tooltip} />
      </Box>
      <Typography variant='caption' sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
        {formatValue(value)}
      </Typography>
    </Box>
    <Slider
      value={value}
      onChange={(_, v) => onChange(v as number)}
      min={min}
      max={max}
      step={step}
      size='small'
      disabled={disabled}
      sx={{
        color: 'text.primary',
        py: 0.5,
        '& .MuiSlider-thumb': { width: 14, height: 14 },
        '& .MuiSlider-rail': { opacity: 0.26 },
      }}
    />
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -0.5 }}>
      <Typography variant='caption' sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
        {minLabel}
      </Typography>
      <Typography variant='caption' sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
        {maxLabel}
      </Typography>
    </Box>
  </Box>
)

const PhonicVoiceDropdown: React.FC<{
  voices: PhonicVoice[]
  selectedVoiceId: string | null
  onSelect: (voice: PhonicVoice | null) => void
  placeholder?: string
}> = ({ voices, selectedVoiceId, onSelect, placeholder = 'Select a voice (optional)' }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const dropdownListRef = useRef<HTMLDivElement>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null)

  const selectedVoice = voices.find((v) => v.id === selectedVoiceId) ?? null
  const filtered = voices.filter((v) => v.name.toLowerCase().includes(searchTerm.toLowerCase()))

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.src = ''
      audioRef.current = null
    }
    setPlayingVoiceId(null)
  }, [])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      const t = e.target as Node
      if (!dropdownRef.current?.contains(t) && !dropdownListRef.current?.contains(t)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return (): void => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    const handleScroll = (e: Event): void => {
      if (dropdownListRef.current?.contains(e.target as Node)) return
      setIsOpen(false)
    }
    window.addEventListener('scroll', handleScroll, true)
    return (): void => window.removeEventListener('scroll', handleScroll, true)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) stopAudio()
    return stopAudio
  }, [isOpen, stopAudio])

  const handlePreview = (e: React.MouseEvent, voice: PhonicVoice): void => {
    e.stopPropagation()
    stopAudio()
    if (playingVoiceId === voice.id) return

    const audio = new Audio(voice.audio_url)
    audioRef.current = audio
    setPlayingVoiceId(voice.id)
    audio.addEventListener('ended', (): void => {
      if (audioRef.current === audio) {
        setPlayingVoiceId(null)
        audioRef.current = null
      }
    })
    audio.addEventListener('error', (): void => {
      if (audioRef.current === audio) {
        setPlayingVoiceId(null)
        audioRef.current = null
      }
    })
    audio.play()
  }

  return (
    <Box sx={{ width: '100%' }}>
      <div
        className={`org-selector ${isOpen ? 'active' : ''}`}
        ref={dropdownRef}
        style={{ width: '100%' }}
      >
        <div
          className='org-selector-header'
          onClick={() => setIsOpen((p) => !p)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: '8px 12px',
            border: '1px solid rgba(0,0,0,0.23)',
            borderRadius: '4px',
            cursor: 'pointer',
            backgroundColor: 'white',
            minHeight: '40px',
            boxSizing: 'border-box',
          }}
        >
          <RecordVoiceOverIcon sx={{ width: 20, height: 20, color: '#666', mr: 1 }} />
          <p
            style={{
              margin: 0,
              flex: 1,
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              fontSize: '14px',
              color: selectedVoice ? 'inherit' : '#666',
            }}
          >
            {selectedVoice ? selectedVoice.name : placeholder}
          </p>
          {selectedVoice && (
            <span
              onClick={(e) => {
                e.stopPropagation()
                onSelect(null)
              }}
              style={{
                cursor: 'pointer',
                color: '#666',
                fontSize: '20px',
                padding: '0 8px',
                lineHeight: 1,
              }}
            >
              ×
            </span>
          )}
          <div style={{ display: 'flex', alignItems: 'center' }} className='dropdown-arrow'>
            <ChevronDownIcon width={12} />
          </div>
        </div>

        {isOpen &&
          ReactDOM.createPortal(
            <div
              ref={dropdownListRef}
              className='org-selector-list'
              style={{
                position: 'fixed',
                zIndex: 1500,
                backgroundColor: 'white',
                boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                borderRadius: '4px',
                maxHeight: '300px',
                overflowY: 'auto',
                width: dropdownRef.current ? `${dropdownRef.current.offsetWidth}px` : '250px',
                top: dropdownRef.current
                  ? `${dropdownRef.current.getBoundingClientRect().bottom}px`
                  : '0',
                left: dropdownRef.current
                  ? `${dropdownRef.current.getBoundingClientRect().left}px`
                  : '0',
              }}
            >
              <Box
                sx={{ p: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 0.5,
                    border: '1px solid rgba(0,0,0,0.23)',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                  }}
                >
                  <SearchIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
                  <input
                    value={searchTerm}
                    onChange={(e) => {
                      e.stopPropagation()
                      setSearchTerm(e.target.value)
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    placeholder='Search voices...'
                    autoFocus
                    style={{
                      border: 'none',
                      outline: 'none',
                      fontSize: 14,
                      flex: 1,
                      background: 'transparent',
                      padding: '2px 0',
                    }}
                  />
                </Box>
              </Box>
              {filtered.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                    No voices found
                  </Typography>
                </Box>
              ) : (
                filtered.map((voice) => (
                  <div
                    key={voice.id}
                    className='org-selector-item'
                    onClick={() => {
                      setIsOpen(false)
                      onSelect(voice)
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 12px',
                      cursor: 'pointer',
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant='body2' sx={{ fontWeight: 500 }}>
                        {voice.name}
                      </Typography>
                      {voice.description && (
                        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                          {voice.description}
                        </Typography>
                      )}
                    </Box>
                    <IconButton size='small' onClick={(e) => handlePreview(e, voice)}>
                      {playingVoiceId === voice.id ? (
                        <Icon name='Pause' fontSize='small' />
                      ) : (
                        <Icon name='PlayArrow' fontSize='small' />
                      )}
                    </IconButton>
                  </div>
                ))
              )}
            </div>,
            document.body,
          )}
      </div>
    </Box>
  )
}

const KeywordInput: React.FC<{
  keywords: string[]
  onChange: (keywords: string[]) => void
  placeholder?: string
}> = ({ keywords, onChange, placeholder = 'Type and press Enter...' }) => {
  const [draft, setDraft] = useState('')

  const commit = (): void => {
    const trimmed = draft.trim()
    if (trimmed && !keywords.includes(trimmed)) {
      onChange([...keywords, trimmed])
    }
    setDraft('')
  }

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      commit()
    } else if (e.key === 'Backspace' && draft === '' && keywords.length > 0) {
      onChange(keywords.slice(0, -1))
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        p: 0.75,
        border: '1px solid rgba(0,0,0,0.23)',
        borderRadius: 1,
        minHeight: 40,
        alignItems: 'center',
        cursor: 'text',
      }}
      onClick={(e) => (e.currentTarget.querySelector('input') as HTMLInputElement)?.focus()}
    >
      {keywords.map((kw) => (
        <Chip
          key={kw}
          label={kw}
          size='small'
          onDelete={() => onChange(keywords.filter((k) => k !== kw))}
          sx={{ height: 24 }}
        />
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commit}
        placeholder={keywords.length === 0 ? placeholder : ''}
        style={{
          border: 'none',
          outline: 'none',
          fontSize: 14,
          flex: 1,
          minWidth: 120,
          background: 'transparent',
          padding: '2px 4px',
        }}
      />
    </Box>
  )
}

const PronunciationEditor: React.FC<{
  entries: PhonicPronunciationDictionaryEntry[]
  onChange: (entries: PhonicPronunciationDictionaryEntry[]) => void
  onRegisterFlush?: (flush: () => void) => void
}> = ({ entries, onChange, onRegisterFlush }) => {
  const [draftWord, setDraftWord] = useState('')
  const [draftPronunciation, setDraftPronunciation] = useState('')

  const addEntry = (): void => {
    const word = draftWord.trim()
    const pronunciation = draftPronunciation.trim()
    if (!word || !pronunciation) return
    if (entries.some((e) => e.word.toLowerCase() === word.toLowerCase())) return
    onChange([...entries, { word, pronunciation }])
    setDraftWord('')
    setDraftPronunciation('')
  }

  useEffect(() => {
    onRegisterFlush?.(addEntry)
  }, [draftWord, draftPronunciation, entries])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {entries.map((entry, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MuiTextField
            size='small'
            value={entry.word}
            onChange={(e) => {
              const updated = [...entries]
              updated[i] = { ...entry, word: e.target.value }
              onChange(updated)
            }}
            placeholder='Word'
            sx={{ flex: 1 }}
          />
          <Typography variant='caption' sx={{ color: 'text.secondary', flexShrink: 0 }}>
            →
          </Typography>
          <MuiTextField
            size='small'
            value={entry.pronunciation}
            onChange={(e) => {
              const updated = [...entries]
              updated[i] = { ...entry, pronunciation: e.target.value }
              onChange(updated)
            }}
            placeholder='Pronunciation'
            sx={{ flex: 1 }}
          />
          <IconButton
            size='small'
            onClick={() => onChange(entries.filter((_, j) => j !== i))}
            sx={{ color: 'error.main', flexShrink: 0 }}
          >
            <Icon name='delete' fontSize='small' />
          </IconButton>
        </Box>
      ))}

      {/* Add row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MuiTextField
          size='small'
          value={draftWord}
          onChange={(e) => setDraftWord(e.target.value)}
          placeholder='Word'
          sx={{ flex: 1 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') addEntry()
          }}
        />
        <Typography variant='caption' sx={{ color: 'text.secondary', flexShrink: 0 }}>
          →
        </Typography>
        <MuiTextField
          size='small'
          value={draftPronunciation}
          onChange={(e) => setDraftPronunciation(e.target.value)}
          placeholder='Pronunciation (e.g. Poo-ket)'
          sx={{ flex: 1 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') addEntry()
          }}
        />
        <IconButton
          size='small'
          onClick={addEntry}
          disabled={!draftWord.trim() || !draftPronunciation.trim()}
          sx={{ flexShrink: 0 }}
        >
          <Icon name='add' fontSize='small' />
        </IconButton>
      </Box>
    </Box>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

const VoiceConfigPhonic: React.FC<PhonicVoiceConfigProps> = ({
  voiceId,
  onVoiceChange,
  voices,
  audioSpeed,
  onAudioSpeedChange,
  isWelcomeMessageInterruptible,
  onIsWelcomeMessageInterruptibleChange,
  minWordsToInterrupt,
  onMinWordsToInterruptChange,
  enableAssistantBackchannel,
  onEnableAssistantBackchannelChange,
  assistantBackchannelAggressiveness,
  onAssistantBackchannelAggressivenessChange,
  backgroundNoise,
  onBackgroundNoiseChange,
  backgroundNoiseLevel,
  onBackgroundNoiseLevelChange,
  boostedKeywords,
  onBoostedKeywordsChange,
  pronunciationDictionary,
  onPronunciationDictionaryChange,
  onRegisterPronunciationFlush,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
    {/* ── Voice | Speed ────────────────────────────────────────────── */}
    <Box sx={{ ...ROW_SX, alignItems: 'start' }}>
      <SettingField label='Voice' tooltip={TOOLTIPS.voice}>
        <PhonicVoiceDropdown
          voices={voices}
          selectedVoiceId={voiceId}
          onSelect={(v) => onVoiceChange(v ? v.id : null)}
        />
      </SettingField>
      <InlineSlider
        label='Speed'
        tooltip={TOOLTIPS.speed}
        value={audioSpeed ?? 1.2}
        onChange={onAudioSpeedChange}
        min={0.5}
        max={1.5}
        step={0.1}
        minLabel='Slower'
        maxLabel='Faster'
        formatValue={(v) => v.toFixed(1)}
      />
    </Box>

    {/* ── Background noise | Noise level ───────────────────────────── */}
    <Box sx={{ ...ROW_SX, alignItems: 'start' }}>
      <SettingField label='Background noise' tooltip={TOOLTIPS.backgroundNoise}>
        <Dropdown
          label=''
          id='phonic-background-noise'
          value={backgroundNoise ?? 'none'}
          options={[
            { label: 'None', value: 'none' },
            { label: 'Office', value: 'office' },
            { label: 'Call center', value: 'call-center' },
            { label: 'Coffee shop', value: 'coffee-shop' },
          ]}
          onChange={(v) =>
            onBackgroundNoiseChange(v === 'none' ? null : (v as PhonicBackgroundNoise))
          }
        />
      </SettingField>
      <InlineSlider
        label='Noise level'
        tooltip={TOOLTIPS.noiseLevel}
        value={backgroundNoiseLevel}
        onChange={onBackgroundNoiseLevelChange}
        disabled={backgroundNoise === null}
        min={0}
        max={0.25}
        step={0.01}
        minLabel='Quiet'
        maxLabel='Loud'
        formatValue={(v) => `${(v * 100).toFixed(0)}%`}
      />
    </Box>

    {/* ── Interruptible greeting | Min words ───────────────────────── */}
    <Box sx={{ ...ROW_SX, alignItems: 'center' }}>
      <FormControlLabel
        control={
          <Switch
            size='small'
            checked={isWelcomeMessageInterruptible}
            onChange={(e) => onIsWelcomeMessageInterruptibleChange(e.target.checked)}
          />
        }
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant='body2'>Interruptible greeting</Typography>
            <InfoTooltip title={TOOLTIPS.interruptibleGreeting} />
          </Box>
        }
      />
      <InlineSlider
        label='Min words to interrupt'
        tooltip={TOOLTIPS.minWordsToInterrupt}
        value={minWordsToInterrupt}
        onChange={(v) => onMinWordsToInterruptChange(Math.round(v))}
        min={1}
        max={10}
        step={1}
        minLabel='1'
        maxLabel='10'
        formatValue={(v) => String(Math.round(v))}
      />
    </Box>

    {/* ── Backchannel | Aggressiveness ─────────────────────────────── */}
    <Box sx={{ ...ROW_SX, alignItems: 'center' }}>
      <FormControlLabel
        control={
          <Switch
            size='small'
            checked={enableAssistantBackchannel}
            onChange={(e) => onEnableAssistantBackchannelChange(e.target.checked)}
          />
        }
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant='body2'>
              Backchannel{' '}
              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                (e.g. "mm-hmm")
              </Typography>
            </Typography>
            <InfoTooltip title={TOOLTIPS.backchannel} />
          </Box>
        }
      />
      <InlineSlider
        label='Backchannel aggressiveness'
        tooltip={TOOLTIPS.backchannelAggressiveness}
        value={assistantBackchannelAggressiveness}
        onChange={onAssistantBackchannelAggressivenessChange}
        disabled={!enableAssistantBackchannel}
        min={0}
        max={1}
        minLabel='Subtle'
        maxLabel='Frequent'
        formatValue={(v) => `${(v * 100).toFixed(0)}%`}
      />
    </Box>

    {/* ── Speech recognition ───────────────────────────────────────── */}
    <SettingField label='Boosted keywords' tooltip={TOOLTIPS.boostedKeywords}>
      <KeywordInput
        keywords={boostedKeywords}
        onChange={onBoostedKeywordsChange}
        placeholder='Type a keyword and press Enter...'
      />
    </SettingField>

    <SettingField label='Pronunciation dictionary' tooltip={TOOLTIPS.pronunciation}>
      <PronunciationEditor
        entries={pronunciationDictionary}
        onChange={onPronunciationDictionaryChange}
        onRegisterFlush={onRegisterPronunciationFlush}
      />
    </SettingField>
  </Box>
)

export default VoiceConfigPhonic
